import { Link } from "react-router-dom";
import { PackageSearch, BadgePercent, Truck, ShieldCheck, UserCheck, LifeBuoy } from "lucide-react";
import { MaskLines, Reveal } from "@/components/site/Reveal";

const offers = [
  { icon: PackageSearch, title: "A hand-picked catalog", text: "Only the products people are actually buying — across audio, beauty, style, home, tech and fitness." },
  { icon: BadgePercent, title: "Fair, honest prices", text: "Compare-at prices shown on sale items, so you always know what you're saving." },
  { icon: Truck, title: "Fast, free shipping", text: "Free on orders over $150, a flat $9 below that — straight to your door." },
  { icon: ShieldCheck, title: "Secure payments", text: "Pay by debit or credit card through Stripe, or with PayPal. Both fully encrypted." },
  { icon: UserCheck, title: "Guest or account", text: "Check out as a guest in under a minute, or create an account to track every order." },
  { icon: LifeBuoy, title: "Real support", text: "Email melic@melicshop.com or call (484) 493-2756 — a person answers, not a robot." },
];

export default function About() {
  return (
    <div className="pb-32" data-testid="about-page">
      <section className="grain relative overflow-hidden">
        <div className="mx-auto max-w-[1440px] px-5 pt-12 pb-16 md:px-10 md:pt-20 md:pb-24">
          <p className="eyebrow text-[var(--terra-deep)]">About us</p>
          <MaskLines
            as="h1"
            testid="about-headline"
            className="font-display mt-4 text-5xl leading-[0.98] tracking-tight sm:text-6xl lg:text-7xl"
            lines={[
              { text: "The store that stocks" },
              { text: "what everyone's", className: "italic text-[var(--terra)]" },
              { text: "talking about." },
            ]}
          />
          <Reveal className="mt-10 grid gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <p className="max-w-2xl text-base leading-relaxed text-[var(--ink-soft)] md:text-lg">
                Melic started with a simple frustration: the best products are everywhere except in one place. Headphones over here, the serum your friends keep recommending over there, the tumbler everyone carries somewhere else entirely.
              </p>
              <p className="mt-6 max-w-2xl text-base leading-relaxed text-[var(--ink-soft)]">
                So we built the edit instead — a small, considered store that stocks the most-wanted items across every aisle of life, keeps the prices honest, and answers when you write. No endless catalog, no filler. Just the good stuff.
              </p>
              <div className="mt-10 flex gap-4">
                <Link to="/shop" className="rounded-full bg-[var(--ink)] px-7 py-3.5 text-sm font-semibold text-[var(--bone)] transition-colors hover:bg-[var(--terra-deep)]" data-testid="about-shop-link">Shop the edit</Link>
                <Link to="/returns" className="link-u self-center text-sm font-semibold" data-testid="about-returns-link">Our return promise</Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 lg:col-span-5">
              <img src="/img/chrono2.jpg" alt="Chrono Minimal Watch" className="aspect-square w-full rounded-2xl object-cover" />
              <img src="/img/candle.jpg" alt="Ember Soy Candle" className="mt-8 aspect-square w-full rounded-2xl object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 md:px-10">
        <Reveal className="mb-12">
          <p className="eyebrow text-[var(--terra-deep)]">What we offer</p>
          <h2 className="font-display mt-4 text-4xl tracking-tight md:text-6xl">Everything you need, <span className="italic">nothing</span> you don't.</h2>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {offers.map(({ icon: Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 0.06} y={24} className="h-full rounded-2xl border border-[var(--line)] bg-white/50 p-6 md:p-8">
              <Icon size={24} className="text-[var(--terra-deep)]" />
              <h3 className="font-display mt-4 text-2xl">{title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">{text}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto mt-20 max-w-[1440px] px-5 md:px-10">
        <Reveal className="rounded-[2rem] bg-[var(--ink)] p-8 text-[var(--bone)] md:p-14">
          <p className="eyebrow text-[var(--terra)]">The Melic promise</p>
          <h2 className="font-display mt-4 max-w-3xl text-3xl leading-tight md:text-5xl">Love it, or send it back — <span className="italic text-[var(--terra)]">within 30 days.</span></h2>
          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-stone-300 md:text-base">Every order ships with our no-fine-print return promise. If something's not right, we make it right.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/shop" className="rounded-full bg-[var(--terra)] px-7 py-3.5 text-sm font-semibold text-[#241a06] transition-colors hover:bg-[var(--terra-deep)]" data-testid="about-cta-shop">Start shopping</Link>
            <a href="mailto:melic@melicshop.com" className="link-u self-center text-sm font-semibold" data-testid="about-cta-email">melic@melicshop.com</a>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
